// src/pages/FieldPage.jsx
//
// AI PRIME FieldAssist — the mobile shell.
//
// ── WHY THERE IS NO NAV PARADIGM HERE ───────────────────────────────────────
//
// One product. A tab bar with one tab, or a drawer holding a single link, is
// chrome that exists to describe a structure that does not exist yet — and once
// shipped it is the thing the second product has to be squeezed into. So the
// shell is a header and a list of cards. The second field product is a second
// card, which is a registration rather than a rework.
//
// Same field constraints as RoutePage: full viewport, high contrast for
// sunlight, every target at least 44pt and the primary one far larger because
// it is pressed with gloves on, and nothing that depends on hover.
//
// ── WHAT THE SHELL CANNOT KNOW ──────────────────────────────────────────────
//
// Whether this org is licensed for roads. `/auth/me` carries no entitlement,
// and the only endpoint that exposes feature grants is platform-admin only, so
// a field account cannot read it. The analytics products the app already holds
// are a different axis entirely — `analytics_entitlement_grants.product_code`,
// not `feature_entitlement_grant_categories.category`.
//
// A probe was considered and rejected: entitlement is checked before the build
// and snap checks, so any valid body would return 403-or-not — but the grant
// predicate is `aoi_scope = 'all' OR ST_Intersects(grant_aoi, both points)`, and
// the shell has no real coordinates to offer. Dummy ones work only while every
// grant is unscoped, which is true on this database today and is exactly the
// kind of check that passes on a fixture and lies later.
//
// So the card navigates, and the FIRST refusal teaches it. useRoutingStore
// keeps `notEntitled` for the session; after a real 403 the card renders
// disabled with the reason instead of sending someone into the same dead end
// again.

import { Navigation, ArrowLeft, Lock, LayoutGrid } from "lucide-react";
import { useAppNav } from "../hooks/useAppNav";
import { useRoutingStore } from "../stores/useRoutingStore";
import { useSiteAgentStore } from "../stores/useSiteAgentStore";

export default function FieldPage() {
  const nav = useAppNav();
  const notEntitled = useRoutingStore((s) => s.notEntitled);
  const siteNotEntitled = useSiteAgentStore((s) => s.notEntitled);

  return (
    <div className="w-full h-full overflow-y-auto bg-slate-950 text-white select-none flex flex-col">
      <header className="shrink-0 px-4 pt-5 pb-4 border-b-2 border-slate-800 flex items-center gap-3">
        <button
          type="button"
          onClick={() => nav.goHome()}
          aria-label="Back to AI PRIME"
          className="shrink-0 min-h-[44px] min-w-[44px] rounded-xl bg-slate-900 border-2 border-slate-700 flex items-center justify-center active:bg-slate-700"
        >
          <ArrowLeft size={22} />
        </button>
        <div>
          <div className="text-[13px] font-bold font-mono uppercase tracking-[0.18em] text-slate-400 leading-none">
            AI PRIME
          </div>
          <h1 className="text-[28px] font-black leading-tight">FieldAssist</h1>
        </div>
      </header>

      {/* The card list. A second product is a second <li> — and SiteAgent
          below is that claim being cashed rather than restated. */}
      <ul className="flex-1 p-4 space-y-4">
        <li>
          <button
            type="button"
            onClick={() => nav.goRoute()}
            disabled={notEntitled}
            aria-describedby={notEntitled ? "route-unavailable" : undefined}
            // Deliberately enormous. This is pressed one-handed, in a truck,
            // wearing gloves — 44pt is the floor for a control, not the size
            // for the one thing this screen exists to do.
            className={`w-full min-h-[132px] rounded-2xl border-2 px-6 py-5 flex items-center gap-5 text-left ${
              notEntitled
                ? "bg-slate-900 border-slate-800 text-slate-500"
                : "bg-cyan-400 border-cyan-200 text-slate-950 active:bg-cyan-300"
            }`}
          >
            <span
              className={`shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center ${
                notEntitled ? "bg-slate-800" : "bg-slate-950/15"
              }`}
            >
              {notEntitled ? <Lock size={30} /> : <Navigation size={32} />}
            </span>
            <span className="min-w-0">
              <span className="block text-[30px] font-black leading-none">Route Me</span>
              <span className="block text-[15px] font-semibold mt-1.5 leading-snug">
                {notEntitled
                  ? "Not included in your subscription"
                  : "Drive to a well pad on mapped roads"}
              </span>
            </span>
          </button>

          {notEntitled && (
            <p
              id="route-unavailable"
              className="mt-2 px-1 text-[15px] font-semibold text-amber-300 leading-snug"
            >
              Routing needs the linear infrastructure layer, which this
              organization is not licensed for. Contact your administrator to
              have it added.
            </p>
          )}
        </li>

        <li>
          <button
            type="button"
            onClick={() => nav.goSite()}
            disabled={siteNotEntitled}
            aria-describedby={siteNotEntitled ? "site-unavailable" : undefined}
            // Same sizing as Route Me, deliberately. Two products of equal
            // standing on one shell; making the newer one smaller would say
            // something about importance that is not true, and the gloved-thumb
            // argument does not weaken because there are now two cards.
            className={`w-full min-h-[132px] rounded-2xl border-2 px-6 py-5 flex items-center gap-5 text-left ${
              siteNotEntitled
                ? "bg-slate-900 border-slate-800 text-slate-500"
                : "bg-cyan-400 border-cyan-200 text-slate-950 active:bg-cyan-300"
            }`}
          >
            <span
              className={`shrink-0 w-16 h-16 rounded-2xl flex items-center justify-center ${
                siteNotEntitled ? "bg-slate-800" : "bg-slate-950/15"
              }`}
            >
              {siteNotEntitled ? <Lock size={30} /> : <LayoutGrid size={32} />}
            </span>
            <span className="min-w-0">
              <span className="block text-[30px] font-black leading-none">SiteAgent</span>
              <span className="block text-[15px] font-semibold mt-1.5 leading-snug">
                {siteNotEntitled
                  ? "Not included in your subscription"
                  : "See what is on a well pad"}
              </span>
            </span>
          </button>

          {siteNotEntitled && (
            <p
              id="site-unavailable"
              className="mt-2 px-1 text-[15px] font-semibold text-amber-300 leading-snug"
            >
              SiteAgent is not included in this subscription. Contact your
              administrator to have it added.
            </p>
          )}
        </li>
      </ul>

      <footer className="shrink-0 px-4 py-3 border-t-2 border-slate-800 text-[13px] font-semibold text-slate-400 leading-snug">
        Advisory only. Nothing here is verified for navigation.
      </footer>
    </div>
  );
}
